import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '$lib/lib/cn';

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  count?: number;
  onClear?: () => void;
  icon?: ReactNode;
}

// Filter chip — pill toggle with optional active count and clear affordance.
// Used by FilterChipStrip on the table view (decision 6).
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { active = false, count, onClear, icon, className, children, ...rest },
  ref
) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center rounded-sm border text-xs transition-colors duration-fast',
        active
          ? 'bg-accent/10 text-accent border-accent/45'
          : 'bg-surface text-fg-muted border-rule-strong hover:bg-surface-2 hover:text-fg'
      )}
    >
      <button
        ref={ref}
        type="button"
        className={cn('inline-flex items-center gap-1.5 px-2.5 py-0', className)}
        aria-pressed={active}
        {...rest}
      >
        {icon}
        <span>{children}</span>
        {typeof count === 'number' && count > 0 && (
          <span className="num ml-1 rounded-xs bg-accent/15 px-1 text-2xs text-accent">{count}</span>
        )}
      </button>
      {active && onClear && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          aria-label={t('filters.clearFilter')}
          className="grid h-7 w-7 place-items-center border-l border-accent/30 text-accent hover:bg-accent/10"
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
});
